<template>
  <div
    data-component-group="auth-form"
    data-component="forgot-pass"
    :class="generateTestClass('form')"
  >
    <Form
      :title="$t('views_reset_second_title')"
      :subtitle="$t('views_reset_subtitle')"
      :on-key-down="onKeyDown"
    >
      <v-text-field
        v-model="email"
        :class="generateTestClass('form-field-email')"
        prepend-inner-icon="mail"
        :label="$t('email_placeholder')"
        required
        type="text"
        :autofocus="true"
        :error-messages="emailErrors"
        @blur="v$.email.$touch()"
      />

      <RecaptchaV2
        v-if="hasRecaptcha"
        :sitekey="apiKey"
        size="invisible"
        @widget-id="onWidgetId"
        @load-callback="onVerify"
      />

      <div class="form-actions">
        <v-btn
          color="primary"
          class="trigger"
          :class="generateTestClass('form-btn-submit')"
          :loading="isLoading"
          :disabled="isDisabled"
          @click="submit"
        >
          <v-icon> email </v-icon>
          {{ $t('components_forms_reset_submit') }}
        </v-btn>
      </div>
    </Form>
  </div>
</template>

<script>
  import { useVuelidate } from '@vuelidate/core'
  import { required, email } from '@vuelidate/validators';
  import { RecaptchaV2, useRecaptcha } from 'vue3-recaptcha-v2';
  import testClassMixin from '../../../mixins/testClass';
  import Form from '../Form';

  export default {
    components: {
      Form,
      RecaptchaV2,
    },
    setup () {
      const { handleExecute, handleReset } = useRecaptcha();
      return { v$: useVuelidate(), handleExecute, handleReset }
    },
    mixins: [testClassMixin],
    validations: {
      email: {
        required,
        email,
      },
    },
    props: {
      hasRecaptcha: {
        type: Boolean,
        default: false,
      },
    },
    data: () => ({
      email: '',
      recaptcha: '',
      widgetId: null,
      apiKey: import.meta.env.VUE_APP_RECAPTCHA_KEY,
      isLoading: false,
      isSubmitted: false,

    }),
    computed: {
      emailErrors() {
        return this.v$.email.$errors.map(error => {
          if (error.$validator === 'required') {
            return this.$t('email_required');
          }
          if (error.$validator === 'email') {
            return this.$t('email_valid');
          }
          return error.$message;
        });
      },
      isDisabled() {
        return this.email === '';
      },
    },
    created() {
      this.$store.watch(
        () => {
          const hasErrors = this.$store.getters['alerts/error'].length > 0;

          if (this.isSubmitted && hasErrors) {
            this.isSubmitted = false;
            this.isLoading = false;
          }
        },
        () => {},
      );
    },
    methods: {
      triggerInvisibleRecaptcha() {
        this.handleExecute(this.widgetId);
      },
      submit() {
        this.v$.$touch();

        if (!this.canSubmitRequest() || this.v$.pending || this.v$.invalid) {
          return false;
        }

        if (this.hasRecaptcha) {
          this.triggerInvisibleRecaptcha();
        } else {
          this.sendPassResetRequest();
        }
      },
      clear() {
        this.v$.$reset();
        this.email = '';
        this.isSubmitted = false;
      },
      canSubmitRequest() {
        return this.email !== '';
      },
      sendPassResetRequest() {
        this.isLoading = true;
        this.isSubmitted = true;
        return this.$store
          .dispatch('auth/sendPassResetRequest', this.email)
          .catch(() => {
            this.isLoading = false;
            this.isSubmitted = false;
            this.handleReset(this.widgetId);
          });
      },
      onWidgetId(widgetId) {
        this.widgetId = widgetId;
      },
      onKeyDown({ key }) {
        if (key === 'Enter') {
          this.submit();
        }
      },
      onVerify(res) {
        this.recaptcha = res;
        return this.sendPassResetRequest();
      },
    },
  };
</script>

<style lang="scss" scoped>
  @use '../../../assets/styles/theme' as *;

  [data-component-group='auth-form'] {
    &[data-component='forgot-pass'] {
      width: 40%;

      [data-component='form'] {
        width: 44%;
        margin: 0 auto;
        padding: 29.5vh 0 100px;
      }
    }
  }
</style>
